import { useState, useEffect, useMemo } from 'react';
import { TagData, TagResponse } from 'src/store/signup-workflow/types';
import { setChallengeStartDate, setChallengeEndDate } from 'src/store/signup';
import paymentAPI from 'src/pages/deprecated/payment/api';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';

export const useChallengeProducts = () => {
  const dispatch = useAppDispatch();
  const [challengeData, setChallengeOption] = useState<TagData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Read challenge dates from Redux first
  const reduxChallengeStartDate = useAppSelector(
    state => state.signUp.challengeStartDate
  );
  const reduxChallengeEndDate = useAppSelector(
    state => state.signUp.challengeEndDate
  );

  useEffect(() => {
    const fetchChallengeData = async () => {
      try {
        setIsLoading(true);

        const { data } = await paymentAPI.post<TagResponse>('/challengeOptIn');

        if (!data?.data?.length) {
          return null;
        }
        const challenge = data.data[0];
        const challengeWithParsedAssets = {
          ...challenge,
          asset_metadata: JSON.parse(
            (challenge?.asset_metadata as unknown as string) || '{}'
          ),
        };

        setChallengeOption(challengeWithParsedAssets);

        // Save challenge dates to Redux for persistence across components
        if (challenge.challenge_start_date) {
          dispatch(setChallengeStartDate(challenge.challenge_start_date));
        }
        if (challenge.challenge_end_date) {
          dispatch(setChallengeEndDate(challenge.challenge_end_date));
        }
      } catch (error) {
        console.error('Error fetching challenge data:', error);
        // Clear challenge dates from Redux on error
        dispatch(setChallengeStartDate(''));
        dispatch(setChallengeEndDate(''));
      } finally {
        setIsLoading(false);
      }
    };

    fetchChallengeData();
  }, [dispatch]);

  const showChallengeProducts = useMemo(() => {
    if (!challengeData?.id) {
      return false;
    }

    return Boolean(challengeData?.allow_challenge_purchase);
  }, [challengeData]);

  const challengeStartDate = useMemo(() => {
    // Return API value if available and loaded, otherwise return Redux value
    const apiStartDate = challengeData?.challenge_start_date || '';
    return apiStartDate || reduxChallengeStartDate || '';
  }, [challengeData?.challenge_start_date, reduxChallengeStartDate]);

  const challengeEndDate = useMemo(() => {
    // Return API value if available and loaded, otherwise return Redux value
    const apiEndDate = challengeData?.challenge_end_date || '';
    return apiEndDate || reduxChallengeEndDate || '';
  }, [challengeData?.challenge_end_date, reduxChallengeEndDate]);

  return {
    challengeData,
    isLoading,
    showChallengeProducts,
    challengeStartDate,
    challengeEndDate,
  };
};
